import React, { useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, ArrowUpRight, Check, AlertCircle } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import PageHero from "../components/PageHero";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const CONTACT_ENDPOINT = "/api/contact.php";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 },
  }),
};

const initialForm = { name: "", email: "", phone: "", service: "", message: "", website: "" };

export default function ContactPage() {
  const { t, contactInfo, serviceCards } = useLanguage();
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const serviceOptions = useMemo(
    () => [...serviceCards.map((service) => service.label), t.contact.form.service_other],
    [serviceCards, t.contact.form.service_other]
  );

  const onChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setStatus({ state: "error", msg: t.contact.form.error });
      return;
    }
    setStatus({ state: "submitting", msg: "" });
    try {
      await axios.post(CONTACT_ENDPOINT, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        service: form.service || null,
        message: form.message.trim(),
        website: form.website,
      });
      setStatus({ state: "success", msg: t.contact.form.success });
      setForm(initialForm);
    } catch (err) {
      const code = err?.response?.status;
      const msg =
        code === 429 ? t.contact.form.rate_limited : t.contact.form.error;
      setStatus({ state: "error", msg });
    }
  };

  return (
    <main className="bg-white" data-testid="contact-page">
      <PageHero
        variant="plain"
        eyebrow={t.contact.eyebrow}
        title={t.contact.title}
        description={t.contact.subtitle}
        testId="contact-hero"
      />

      {/* Form + Info */}
      <section className="pb-28 sm:pb-36">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 grid gap-10 lg:grid-cols-12">
          <motion.form
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            onSubmit={onSubmit}
            className="relative lg:col-span-7 rounded-[2.5rem] bg-[#fbd9e5]/50 border border-[#3e2723]/8 p-8 sm:p-12"
            data-testid="contact-form"
          >
            {/* Honeypot: leave empty — bots often fill "website" */}
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={onChange}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="pointer-events-none absolute -left-[9999px] top-0 h-px w-px opacity-0"
            />
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label={t.contact.form.name} name="name" value={form.name} onChange={onChange} required testid="input-name" />
              <Field label={t.contact.form.email} name="email" type="email" value={form.email} onChange={onChange} required testid="input-email" />
              <Field label={t.contact.form.phone} name="phone" value={form.phone} onChange={onChange} testid="input-phone" />
              <ServiceSelectField
                label={t.contact.form.service}
                value={form.service}
                onChange={onChange}
                options={serviceOptions}
                testid="select-service"
              />
              <div className="sm:col-span-2">
                <TextAreaField
                  label={t.contact.form.message}
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  required
                  testid="input-message"
                />
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button
                type="submit"
                disabled={status.state === "submitting"}
                className="inline-flex items-center gap-2 rounded-full bg-[#3e2723] text-[#fbd9e5] px-7 py-4 text-[14px] font-semibold hover:bg-[#2a1a17] hover:-translate-y-0.5 transition-all duration-300 shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="contact-submit"
              >
                {status.state === "submitting" ? t.contact.form.submitting : t.contact.form.submit}
                <ArrowUpRight className="h-4 w-4" />
              </button>

              {status.state === "success" && (
                <span className="inline-flex items-center gap-2 text-[13px] text-[#3e2723]" data-testid="contact-success">
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#3e2723] text-[#fbd9e5]">
                    <Check className="h-3 w-3" />
                  </span>
                  {status.msg}
                </span>
              )}
              {status.state === "error" && (
                <span className="inline-flex items-center gap-2 text-[13px] text-red-700" data-testid="contact-error">
                  <AlertCircle className="h-4 w-4" />
                  {status.msg}
                </span>
              )}
            </div>
          </motion.form>

          <motion.aside
            variants={fadeUp}
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            className="lg:col-span-5 space-y-4"
          >
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#3e2723]/50">
              {t.contact.info_title}
            </p>
            <InfoBox
              icon={Mail}
              label={t.contact.email_label}
              value={contactInfo.email}
              href={`mailto:${contactInfo.email}`}
              testid="info-email"
            />
            <InfoBox
              icon={Phone}
              label={t.contact.phone_label}
              value={contactInfo.phone}
              href={`tel:${contactInfo.phone.replace(/\s/g, "")}`}
              testid="info-phone"
            />
            <InfoBox
              icon={MapPin}
              label={t.contact.location_label}
              value={t.contact.location_value}
              testid="info-location"
            />
            <InfoBox
              icon={Clock}
              label={t.contact.hours_label}
              value={t.contact.hours_value}
              testid="info-hours"
            />
          </motion.aside>
        </div>
      </section>
    </main>
  );
}

function Field({ label, name, type = "text", value, onChange, required, testid }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.2em] text-[#3e2723]/60 font-semibold mb-2">
        {label}
        {required ? " *" : ""}
      </span>
      <input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full rounded-2xl bg-white/70 backdrop-blur-xl border border-[#3e2723]/10 px-5 py-3.5 text-[15px] text-[#3e2723] placeholder:text-[#7a5c55]/50 focus:outline-none focus:ring-2 focus:ring-[#3e2723] focus:border-transparent transition"
        data-testid={testid}
      />
    </label>
  );
}

function ServiceSelectField({ label, value, onChange, options, testid }) {
  return (
    <div className="block">
      <span className="block text-[11px] uppercase tracking-[0.2em] text-[#3e2723]/60 font-semibold mb-2">
        {label}
      </span>
      <Select
        value={value || undefined}
        onValueChange={(nextValue) => onChange({ target: { name: "service", value: nextValue } })}
      >
        <SelectTrigger
          className="h-auto w-full rounded-2xl border border-[#3e2723]/10 bg-white/70 px-5 py-3.5 text-[15px] text-[#3e2723] shadow-none backdrop-blur-xl focus:ring-2 focus:ring-[#3e2723] focus:ring-offset-0 data-[placeholder]:text-[#7a5c55]/50"
          data-testid={testid}
        >
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent className="overflow-hidden rounded-2xl border border-[#3e2723]/10 bg-white p-1.5 shadow-[0_20px_60px_-20px_rgba(62,39,35,0.28)]">
          {options.map((option) => (
            <SelectItem
              key={option}
              value={option}
              className="cursor-pointer rounded-xl py-2.5 pl-4 pr-10 text-[15px] text-[#3e2723] focus:bg-[#fbd9e5]/70 focus:text-[#3e2723]"
            >
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function TextAreaField({ label, name, value, onChange, required, testid }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.2em] text-[#3e2723]/60 font-semibold mb-2">
        {label}
        {required ? " *" : ""}
      </span>
      <textarea
        name={name}
        rows={5}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full rounded-2xl bg-white/70 backdrop-blur-xl border border-[#3e2723]/10 px-5 py-3.5 text-[15px] text-[#3e2723] placeholder:text-[#7a5c55]/50 focus:outline-none focus:ring-2 focus:ring-[#3e2723] focus:border-transparent transition resize-none"
        data-testid={testid}
      />
    </label>
  );
}

function InfoBox({ icon: Icon, label, value, href, testid }) {
  const Inner = (
    <div
      className="group flex items-start gap-4 rounded-[1.5rem] bg-white border border-[#3e2723]/8 p-5 hover:shadow-[0_12px_36px_rgba(62,39,35,0.08)] hover:-translate-y-0.5 transition-all duration-300"
      data-testid={testid}
    >
      <span className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-[#fbd9e5] text-[#3e2723] group-hover:bg-[#3e2723] group-hover:text-[#fbd9e5] transition-colors">
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-[#3e2723]/50">{label}</p>
        <p className="mt-1 text-[15px] font-semibold text-[#3e2723]">{value}</p>
      </div>
    </div>
  );
  return href ? (
    <a href={href} className="block">
      {Inner}
    </a>
  ) : (
    Inner
  );
}
